


































import { Component, Mixins, Watch, Prop, Emit } from "vue-property-decorator";
import {
  Point,
  Size,
  Rectangle,
} from "../../domain/drawing";
import { TextAreaUtility, ITextAreaCharacter } from "../drawing/utilities";
import { BrowserUtility, BrowserKinds, ClipboardUtility } from "../../utilities";
import { ModelEditableMixin } from "./mixins";

class Range {
  constructor(public start: number = 0, public end: number = 0, public direction: "forward" | "backward" | "none" = "forward") {
  }

  get length(): number {
    return this.end - this.start;
  }

  get isValid(): boolean {
    return (0 < this.length);
  }

  get isDirectionForward(): boolean {
    return (this.direction === "forward")
        || (this.direction === "none");
  }
}

@Component
export default class ImelessTextarea extends Mixins(ModelEditableMixin) {
  @Prop()
  textareaStyle: any;

  @Prop({ default: 1 })
  scale: number;

  @Prop({ default: false })
  readonly: boolean;

  @Prop()
  tabindex: number;

  @Prop({ default: true })
  isMultiLine: boolean;

  @Prop()
  validate: (value: string) => boolean;

  @Prop({ default: false })
  autofocus: boolean;

  @Prop({ default: false })
  isPassword: boolean;

  @Emit()
  focus(event: FocusEvent): void {
    this.updateCaretIndication();
    this.isFocused = true;
    document.addEventListener("selectionchange", this.onSelectionChange);
  }

  @Emit()
  blur(event: FocusEvent): void {
    document.removeEventListener("selectionchange", this.onSelectionChange);
    window.getSelection().removeAllRanges();
    this.updateCaretIndication();
    this.isFocused = false;
  }

  @Emit()
  click(event: PointerEvent): void {}

  data() {
    return {
      isFocused: false,
      isTextSelected: false,
      dragStartRange: null as Range,
      isDragOver: false,
      caretRectangle: new Rectangle(new Point(1, 0), new Size(1, 20)),
      characters: [] as ITextAreaCharacter[]
    };
  }
  isFocused: boolean;
  isTextSelected: boolean;
  dragStartRange: Range;
  isDragOver: boolean;
  caretRectangle: Rectangle;
  characters: ITextAreaCharacter[];

  $refs!: {
    editor: HTMLTextAreaElement,
  };

  mounted(): void {
    if (this.isIe) {
      return;
    }
    this.editorText = this.editingSource;
  }

  @Watch("editingSource")
  onEditingSourceUpdated(newValue: string, oldValue: string): void {
    this.editorText = newValue;
  }

  get editorText(): string {
    const source = typeof this.editingSource === "string"
      ? this.editingSource
      : "";
    return this.isPassword
      ? source.replace(/./g, "●")
      : source;
  }

  set editorText(value: string) {
    const replaced = this.isMultiLine
      ? value.replace(/\r\n/g, "\n")    // 改行コードを補正する
      : value.replace(/[\r|\n]/g, "");  // 改行コードを除去する
    if (this.isValid(replaced)) {
      this.editingSource = replaced;
    } else {
      this.$logger.debug("有効でない文字列になるため削除します.: " + replaced);
      this.editingSource = "";
    }
    this.$nextTick(() => {
      this.updateCaretIndication();
    });
  }

  get shouldIndicateCaret(): boolean {
    return (this.isFocused && !this.isTextSelected)
      || this.isDragOver;
  }

  get caretGeometryProperties(): { [ key: string ]: string } {
    return {
      top: (this.caretRectangle.y / this.scale) + "px",
      left: (this.caretRectangle.x / this.scale) + "px",
      width: (this.caretRectangle.width / this.scale) + "px",
      height: (this.caretRectangle.height / this.scale) + "px",
    };
  }

  get backgroundStyle(): { [ key: string ]: string } {
    return this.extractStyle(n => n.startsWith("background"));
  }

  private extractStyle(isValid: (n: string) => boolean): { [ key: string ]: string } {
    const result: { [ key: string ]: string } = {};
    if (!this.textareaStyle) {
      return result;
    }
    const textareaStyle = Array.isArray(this.textareaStyle)
      ? this.textareaStyle
      : [this.textareaStyle];
    for (const style of textareaStyle) {
      for (const key of Object.keys(style)) {
        if (isValid(key)) {
          result[key] = style[key];
        }
      }
    }
    return result;
  }

  get editorStyle(): { [ key: string ]: string } {
    const style = this.extractStyle(n => !n.startsWith("background"));
    // 計測時に文字を分離する仕様上、文字毎に折り返せるよう強制する
    style.wordBreak = "break-all";
    style.wordWrap = "break-word";
    style.whiteSpace = this.isMultiLine
      ? "pre-wrap"
      : "pre";
    return style;
  }

  onKeyInputted(event: KeyboardEvent): void {
    if (this.readonly) {
      if ((35 <= event.keyCode) && (event.keyCode <= 40)) { // cursor.
        this.handleCursorKey(event);
      }
    } else {
      if (!event.ctrlKey && event.key.match(/^.$/)) {
        this.inputString(event.key);
        event.preventDefault(); // chromeでスペースを入力時、スクロールが動くので止める
      } else if (event.keyCode === 8) {   // backspace.
        this.removeString(-1);
      } else if (event.keyCode === 13) {  // return.
        this.inputString("\n");
      } else if ((35 <= event.keyCode) && (event.keyCode <= 40)) {  // cursor.
        this.handleCursorKey(event);
      } else if (event.keyCode === 46) {  // delete.
        this.removeString(0);
      } else if (event.ctrlKey && (event.key === "v")) {  // ctrl + v.
        this.pasteString();
      } else if (event.ctrlKey && (event.key === "x")) {  // ctrl + x.
        if (this.isPassword) {
          event.preventDefault(); // カット出来ないようにする
        } else {
          this.cutString();
        }
      } else if (event.ctrlKey && (event.key === "c")) {  // ctrl + c.
        if (this.isPassword) {
          event.preventDefault(); // コピー出来ないようにする
        }
        // ctrl + c はreadonlyでも処理されるので何もしない.
      }
      // ctrl + a はreadonlyでも処理されるので何もしない.
    }
  }

  onKeyuped(event: KeyboardEvent): void {
    this.updateCaretIndication(); // Firefoxではtextareaの更新がこのタイミングになるためキャレットを更新する
  }

  private handleCursorKey(event: KeyboardEvent): void {
    const offset = this.toOffset(event.keyCode);
    if (event.shiftKey) {
      this.resizeRange(offset);
    } else {
      this.disposeRange(offset);
    }
    event.preventDefault(); // chromeは通常処理しないくせにハンドルしたら止めないとresizeが2倍動くので止める.
  }

  private toOffset(keyCode: number): number {
    const range = this.getSelectedRange();
    switch (keyCode) {
      case 35:  // End.
        return this.toEndOffset(range);
      case 36:  // Home.
        return this.toHomeOffset(range);
      case 37:  // left.
        return this.toLeftOffset(range);
      case 39:  // right.
        return this.toRightOffset(range);
      case 38:  // up.
        return this.toUpOffset(range);
      case 40:  // down.
        return this.toDownOffset(range);
      default:
        return 0;
    }
  }

  private toLeftOffset(range: Range): number {
    return (range.isDirectionForward && range.end === 0) || (!range.isDirectionForward && range.start === 0)
      ? 0
      : -1;
  }

  private toRightOffset(range: Range): number {
    return 1;
  }

  private toHomeOffset(range: Range): number {
    const baseIndex = range.isDirectionForward ? range.end : range.start;
    const char = this.characters[baseIndex];
    if (!char) {
      return 0;
    }
    const filtered = this.characters
      .map((v, i) => {
        return {
          value: v,
          index: i,
        };
      })
      .filter(v => v.value.beginOfLine && v.index <= baseIndex)
      .map(v => v.index);
    // 基準が行頭以外は移動
    return (filtered.length > 0 && Math.max(...filtered) !== baseIndex)
      ? Math.max(...filtered.filter(v => v !== baseIndex)) - baseIndex
      : 0;
  }

  private toEndOffset(range: Range): number {
    const baseIndex = range.isDirectionForward ? range.end : range.start;
    const char = this.characters[baseIndex];
    if (!char) {
      return 0;
    }
    const filtered = this.characters
      .map((v, i) => {
        return {
          value: v,
          index: i,
        };
      })
      .filter(v => v.value.endOfLine && v.index >= baseIndex)
      .map(v => v.index);
    // 基準が行末以外は移動
    return (filtered.length > 0 && Math.min(...filtered) !== baseIndex)
      ? Math.min(...filtered.filter(v => v !== baseIndex)) - baseIndex
      : 0;
  }

  private toUpOffset(range: Range): number {
    const index = this.toVerticalIndex(range, -1);
    if (index === null) {
      return 0;
    }
    return index - (range.isDirectionForward ? range.end : range.start);
  }

  private toDownOffset(range: Range): number {
    const index = this.toVerticalIndex(range, 1);
    if (index === null) {
      return 0;
    }
    return index - (range.isDirectionForward ? range.end : range.start);
  }

  private toVerticalIndex(range: Range, offset: number): number {
    const char = this.characters[range.isDirectionForward ? range.end : range.start];
    if (!char) {
      return null;
    }
    const filtered = this.characters
      .map((v, i) => {
        return {
          value: v,
          index: i,
          distance: Math.abs(char.rectangle.x - v.rectangle.x)
        };
      })
      .filter(v => v.value.verticalIndex === (char.verticalIndex + offset))
      .sort((a, b) => a.distance - b.distance);
    return filtered.length > 0
      ? filtered[0].index
      : null;
  }

  private get isIe(): boolean {
    return (BrowserUtility.getBrowserKind() === BrowserKinds.InternetExplorer);
  }

  private get isFirefox(): boolean {
    return (BrowserUtility.getBrowserKind() === BrowserKinds.MozillaFirefox);
  }

  private resizeRange(offset: number): void {
    const selected = this.getSelectedRange();
    this.$nextTick(() => {
      this.setSelectionRange(this.createResizeRange(selected, offset));
    });
  }

  private createResizeRange(selected: Range, offset: number): Range {
    if (selected.isDirectionForward) {
      const end = selected.end + offset;
      if (selected.start < end) {
        return new Range(selected.start, end, "forward");
      } else if (selected.start > end) {
        return new Range(end, selected.start, "backward");
      } else {
        return new Range(selected.start, selected.start, "none");
      }
    } else {
      const start = selected.start + offset;
      if (start < selected.end) {
        return new Range(start, selected.end, "backward");
      } else if (start > selected.end) {
        return new Range(selected.end, start, "forward");
      } else {
        return new Range(selected.end, selected.end, "none");
      }
    }
  }

  private disposeRange(offset: number): void {
    const selected = this.getSelectedRange();
    this.setCaretIndex(((offset <= 0) ? selected.start : selected.end) + (selected.isValid ? 0 : offset));
  }

  private isValid(key: string): boolean {
    return !this.validate
        || this.validate(key);
  }

  private inputString(value: string): void {
    const replaced = this.isMultiLine
      ? value.replace(/\r\n/g, "\n")    // 改行コードを補正する
      : value.replace(/[\r|\n]/g, "");  // 改行コードを除去する
    this.replaceString(this.getSelectedRange(), replaced);
  }

  private getSelectedRange(): Range {
    const editor = this.getEditor();
    const result = editor
      ? new Range(editor.selectionStart, editor.selectionEnd, editor.selectionDirection as "forward" | "backward" | "none")
      : new Range();
    return result;
  }

  getEditor(): HTMLTextAreaElement {
    return this.$refs.editor;
  }

  private replaceString(range: Range, value: string): void {
    const replaced
      = this.editingSource.slice(0, range.start)
      + value
      + this.editingSource.slice(range.end);
    if (!this.isValid(replaced)) {
      this.$logger.debug("有効でない文字列になるため無視します.: " + replaced);
      return;
    }
    this.editingSource = replaced;
    this.$nextTick(() => {
      this.setCaretIndex(range.start + value.length);
    });
  }

  private setCaretIndex(value: number): void {
    const editor = this.getEditor();
    editor.focus();
    editor.setSelectionRange(value, value);
    this.updateCaretIndication();
    this.updateScroll();
  }

  private updateCaretIndication(): void {
    const selectedRange = this.getSelectedRange();
    this.isTextSelected = (selectedRange.start < selectedRange.end);
    const caretRectangle = TextAreaUtility.getRectangles(this.getEditor());
    this.caretRectangle = caretRectangle.caret;
    this.characters = caretRectangle.characters;
  }

  private updateScroll(): void {
    const resultTop = this.updateScrollTop();
    const resultLeft = this.updateScrollLeft();
    if (resultTop || resultLeft) {
      this.updateCaretIndication();
    }
  }

  private updateScrollTop(): boolean {
    const editor = this.getEditor();
    if (editor.clientHeight < editor.scrollHeight) {
      const selectedRange = this.getSelectedRange();
      const selectedRectangle = this.characters[selectedRange.isDirectionForward ? selectedRange.end : selectedRange.start].rectangle;
      const selectedY = selectedRectangle.y / this.scale;
      const selectedHeight = selectedRectangle.height / this.scale;
      if (selectedY < 0) {
        editor.scrollTop += selectedY;
        return true;
      } else if (editor.clientHeight < (selectedY + selectedHeight)) {
        editor.scrollTop += selectedY + selectedHeight - editor.clientHeight;
        return true;
      }
    }
    return false;
  }

  private updateScrollLeft(): boolean {
    const editor = this.getEditor();
    if (editor.clientWidth < editor.scrollWidth) {
      const selectedRange = this.getSelectedRange();
      const selectedRectangle = this.characters[selectedRange.isDirectionForward ? selectedRange.end : selectedRange.start].rectangle;
      const selectedX = selectedRectangle.x / this.scale;
      const selectedWidth = selectedRectangle.width / this.scale;
      if (selectedX < 0) {
        editor.scrollLeft += selectedX;
        return true;
      } else if (editor.clientWidth < (selectedX + selectedWidth)) {
        editor.scrollLeft += selectedX + selectedWidth - editor.clientWidth;
        return true;
      }
    }
    return false;
  }

  private setSelectionRange(value: Range): void {
    const editor = this.getEditor();
    editor.focus();
    editor.setSelectionRange(value.start, value.end, value.isValid ? value.direction : "forward");
    this.updateCaretIndication();
    this.updateScroll();
  }

  private removeString(offset: number): void {
    const selected = this.getSelectedRange();
    if (selected.isValid) {
      this.replaceString(selected, "");
    } else {
      this.removeChar(selected.start + offset);
    }
  }

  private removeChar(index: number): void {
    if (index < 0) {
      return;
    }
    this.replaceString(new Range(index, index + 1), "");
  }

  private pasteString(): void {
    if (this.isFirefox) { // Firefoxではイベント経由でデータを取得する
      const onPaste = (e: ClipboardEvent) => {
        window.removeEventListener("paste", onPaste);
        this.inputString(e.clipboardData.getData("text"));
      };
      window.addEventListener("paste", onPaste);
    } else {
      ClipboardUtility.createAccessor().readText()
        .then(t => this.inputString(t));
    }
  }

  private cutString(): void {
    const selected = this.getSelectedRange();
    if (selected.isValid) {
      if (this.isFirefox) { // Firefoxではイベント経由で削除部分を行う(コピーは実施されている)
        const onCut = (e: ClipboardEvent) => {
          window.removeEventListener("cut", onCut);
          this.removeString(0);
        };
        window.addEventListener("cut", onCut);
      } else {
        ClipboardUtility.createAccessor().writeSelectedText()
          .then(() => this.removeString(0));
      }
    }
  }

  onSelectionChange(event: Event): void {
    this.updateCaretIndication();
  }

  onDragStart(event: DragEvent): void {
    if (this.isPassword) {
      event.preventDefault(); // ドラッグ出来なようにする
      return;
    }
    this.dragStartRange = this.getSelectedRange();
  }

  onDragEnter(event: DragEvent): void {
    this.updateCaretIndication();
  }

  onDragOver(event: DragEvent): void {
    if (this.readonly || this.isPassword) {
      return;
    }
    if (this.characters.length > 0) {
      const index = this.getNearCharacterIndex(event);
      const notApplicable = !!this.dragStartRange
        ? this.dragStartRange.isValid && this.dragStartRange.start <= index && index <= this.dragStartRange.end
        : false;
      // 選択対象と同じ範囲内は対象外
      if (notApplicable) {
        this.isDragOver = false;
        return;
      }
      const rectangle = this.characters[index].rectangle;
      this.caretRectangle = new Rectangle(
        rectangle.topLeft,
        new Size(1, rectangle.height)
      );
    }
    event.dataTransfer.dropEffect = this.getDropEffect(event);
    event.preventDefault();
    event.stopPropagation();  // 親への通知を止めないとdropEffectによるマウスカーソルが更新されない
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent): void {
    this.isDragOver = false;
  }

  onDragEnd(event: DragEvent): void {
    this.dragStartRange = null;
  }

  onDrop(event: DragEvent): void {
    if (this.readonly) {
      return;
    }
    const nearIndex = this.getNearCharacterIndex(event);
    const text = event.dataTransfer.getData("text");
    if (this.getDropEffect(event) === "copy") {
      this.inputDroppedString(nearIndex, text);
    } else {
      this.removeString(0);
      const offset = !this.dragStartRange || nearIndex < this.dragStartRange.start
        ? 0
        : this.dragStartRange.length;
      this.$nextTick(() => {
        this.inputDroppedString(nearIndex - offset, text);
      });
    }
    event.preventDefault();
    this.dragStartRange = null;
    this.isDragOver = false;
  }

  private getNearCharacterIndex(event: DragEvent): number {
    if (this.characters.length === 0) {
      return 0;
    }
    const target = event.target instanceof HTMLTextAreaElement
      ? { left: event.target.offsetLeft, top: event.target.offsetTop }
      : { left: 0, top: 0 };
    const offsetX = (event.offsetX + target.left) * this.scale;
    const offsetY = (event.offsetY + target.top) * this.scale;
    const sorted = this.characters
      .map((v, i) => ({
        value: v,
        index: i,
        distanceX: Math.abs(offsetX - v.rectangle.x),
        distanceY: Math.abs(offsetY - v.rectangle.y),
      }))
      .sort((a, b) => a.distanceY === b.distanceY
        ? a.distanceX - b.distanceX
        : a.distanceY - b.distanceY);
    return sorted[0].index;
  }

  private getDropEffect(event: DragEvent): "copy" | "move" {
    return !!this.dragStartRange && !event.ctrlKey
      ? "move"
      : "copy";
  }

  private inputDroppedString(index: number, text: string): void {
    this.setCaretIndex(index);
    this.inputString(text);
    this.resizeRange(text.length);
  }

  focusToEditor(): void {
    const editor = this.getEditor();
    editor.focus();
  }
}
